import { useCallback, useEffect, useMemo, useState } from 'react'
import { api } from '../../../lib/api'
import type { Collection, CollectionFormData } from '../types'

type UseCollectionsReturn = {
  collections: Collection[]
  loading: boolean
  error: string | null

  // Actions
  reload: () => Promise<void>
  create: (data: CollectionFormData) => Promise<boolean>
  rename: (id: number, title: string) => Promise<boolean>
  setContentLanguage: (id: number, isoOrNull: string | null) => Promise<boolean>
  remove: (id: number) => Promise<boolean>

  // Helpers
  getById: (id: number) => Collection | undefined
}

/**
 * Hook pour gérer les collections (ex-sujets)
 * Responsabilité unique : CRUD collections
 */
export function useCollections(): UseCollectionsReturn {
  const [collections, setCollections] = useState<Collection[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const { data } = await api.get('/subjects')
      setCollections(data || [])
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Erreur chargement collections')
    } finally {
      setLoading(false)
    }
  }, [])

  const create = useCallback(async (formData: CollectionFormData): Promise<boolean> => {
    if (!formData.title.trim()) return false
    try {
      const body: Record<string, unknown> = { title: formData.title.trim() }
      const cl = formData.content_language
      if (cl !== undefined && cl !== null && String(cl).trim() !== '') {
        body.content_language = String(cl).trim().toLowerCase()
      }
      await api.post('/subjects', body)
      await reload()
      return true
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Erreur création collection')
      return false
    }
  }, [reload])

  const rename = useCallback(async (id: number, title: string): Promise<boolean> => {
    if (!title.trim()) return false
    try {
      await api.patch(`/subjects/${id}`, { title: title.trim() })
      await reload()
      return true
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Erreur renommage collection')
      return false
    }
  }, [reload])

  const setContentLanguage = useCallback(async (id: number, isoOrNull: string | null): Promise<boolean> => {
    try {
      await api.patch(`/subjects/${id}`, {
        content_language: isoOrNull && isoOrNull.trim() ? isoOrNull.trim().toLowerCase() : null,
      })
      await reload()
      return true
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Erreur langue collection')
      return false
    }
  }, [reload])

  const remove = useCallback(async (id: number): Promise<boolean> => {
    try {
      await api.delete(`/subjects/${id}`)
      await reload()
      return true
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Erreur suppression collection')
      return false
    }
  }, [reload])

  const getById = useCallback(
    (id: number) => {
      return collections.find(c => c.id === id)
    },
    [collections],
  )

  // Chargement initial
  useEffect(() => {
    reload()
  }, [reload])

  return useMemo(
    () => ({
      collections,
      loading,
      error,
      reload,
      create,
      rename,
      setContentLanguage,
      remove,
      getById,
    }),
    [collections, loading, error, reload, create, rename, setContentLanguage, remove, getById],
  )
}

export default useCollections
